/**
 * THE BATCH PLAN — the whole screen's breakdowns, in ONE request.
 *
 * Ported from dashboard-analytics.tsx, where the reasoning is recorded and is
 * worth keeping in front of whoever edits this next:
 *
 * WHY THE PANELS ARE ONE REQUEST AND NOT NINE. Every panel is the same window
 * over the same scope, so they share one RLS transaction — which is one pooled
 * connection. Production runs a pool of five, so nine panels as nine requests is
 * nine connections, and three managers opening this screen at once is exactly
 * the concurrency that stalled the whole application. The batch also resolves
 * the scope, the window and the comparison window ONCE and states them at the
 * top level, which is why this screen can print one caption instead of nine that
 * could disagree.
 *
 * WHY META COMES FIRST AND IS NOT PARALLEL WITH IT. The panel list cannot be
 * built without `meta`: each measure carries its OWN legal dimension list, and
 * sending a pair outside it makes the server substitute `building` and say so.
 * The server being honest about the substitution does not make an impossible
 * control acceptable, so the request waits for the descriptor rather than
 * guessing.
 */
import { AnalyticsPanelRequest } from '../../../../../api/analytics-client';
import { DashboardFilter, FilterMeta, Granularity } from '../state/filter-model';

export const PANEL = {
  pulseEntries: 'pulse-entries',
  pulseIncidents: 'pulse-incidents',
  league: 'league',
  composition: 'composition',
  severity: 'severity',
  portfolio: 'portfolio',
  hour: 'hour',
  staff: 'staff',
  /* Incidents per building, for the outlier scatter. The league panel already
     asks for the CHOSEN measure by building; this asks for incidents by the
     same dimension so the two can be plotted against each other. It is one more
     slot in a request that is already being made, not one more request. */
  leagueIncidents: 'league-incidents',

  /* ── THE REPORTS TAB'S TWO CHARTS (QA-03) ────────────────────────────────
   *
   * "People logged each day" and "Types of visitors" used to aggregate
   * `data.records.data.rows` — ONE PAGE of 50 records at the current offset, not
   * the selected period. The tab captioned that honestly ("Drawn from the 50
   * rows on page 1 … not from the whole period"), and an honest caption on a
   * chart titled "People logged each day" is still the wrong number: a reader
   * makes staffing decisions from a materially incomplete sample that looks
   * period-wide.
   *
   * These two slots answer the same two questions over the WHOLE server-side
   * filtered result. They are slots in a batch that is already being sent, so
   * the fix costs no extra round trip and no extra pooled connection.
   *
   * They are SEPARATE from `pulseEntries` / `composition` even though the shapes
   * rhyme: those two follow `filter.measure`, a control the reader sets for the
   * Trends tab, while these are pinned to `people` and narrowed to the selected
   * BOOK. Sharing them would make the Reports charts change meaning when
   * somebody touched an unrelated control on another tab. */
  reportsDaily: 'reports-daily',
  reportsKind: 'reports-kind',
} as const;


export const SPARK_PREFIX = 'spark-';

/** A per-person split of a hundred people is a row dump, not a panel. The server
 *  reports `truncated` and the panel prints how many groups it is showing out of
 *  how many there are. */
export const STAFF_LIMIT = 25;

/** A calendar `day` split has to clear a 31-day month or the chart silently
 *  loses its last days to the limit. */
export const DAY_LIMIT = 400;

/**
 * The Reports tab's own two panels (QA-03).
 *
 * DELIBERATELY NOT IN buildPlan. The batch sends ONE set of entry filters for
 * every panel — that is the design, because they describe the SCREEN rather than
 * a tile — and these two must be narrowed to the selected BOOK. Adding them to
 * the shared plan would mean either narrowing every panel and the KPI strip to
 * the book (silently changing what the headline figures count), or leaving them
 * un-narrowed and charting all four books under a title that names one.
 *
 * So the Reports tab sends its own two-panel batch with `groups` set. That is
 * one extra round trip on ONE tab, which is the honest price of the two charts
 * being about the book the reader selected.
 *
 * ── THE MEASURE IS NOT THE SAME FOR BOTH BOOKS, AND FINDING THAT OUT COST A
 *    ROUND OF THIS FIX ──
 *
 * The obvious reading is that both charts count `people`, because both panels
 * say "counted in people". They cannot. `people` in the engine is
 * `people_admitted` — a VISITOR_LOG predicate, arm for arm, excluding Emergency
 * (911) headcounts and refusals (src/iq/ask-facts.js). Asked for the
 * `daily_log` group it correctly returns ZERO, and measured against the local
 * corpus it did: 0 people over a period holding 1,664 building activities. Had
 * this shipped keyed on `people` for both, the Building Activities charts would
 * have gone from a fifty-row sample to an EMPTY PANEL — a worse bug than the one
 * being fixed, and one that looks like "a quiet month".
 *
 * So the daily log is measured in `entries` — records — and the panel copy in
 * book-charts.tsx says which of the two it is rather than claiming "people" over
 * a figure that counts entries.
 */
export const REPORTS_MEASURE: Record<string, string> = {
  'Visitor Log': 'people',
  'Daily Log': 'entries',
  // Neither of the other two books renders these panels — Parcels and Residents
  // have their own bespoke charts — but the map is total so a caller cannot get
  // `undefined` into a request.
  'Parcel Log': 'entries',
  'Resident Log': 'entries',
};

/** The two panels for one book. A function, not a constant, because the measure
 *  depends on the book — see the note above. */
export const reportsPanels = (book: string): AnalyticsPanelRequest[] => {
  const measure = REPORTS_MEASURE[book] || 'entries';

  return [
    { id: PANEL.reportsDaily, measure, dimension: 'day', limit: DAY_LIMIT },
    { id: PANEL.reportsKind, measure, dimension: 'kind' },
  ];
};

/** Which activity group each book is. The engine's `groups` entry filter is a
 *  predicate over (src, kind) — see src/analytics/entry-filters.js — and these
 *  four are exactly the four books. */
export const BOOK_GROUP: Record<string, string> = {
  'Visitor Log': 'visitors',
  'Resident Log': 'residents',
  'Parcel Log': 'parcels',
  'Daily Log': 'daily_log',
};

/**
 * THE SIX HEADLINE TILES, in display order.
 *
 * A CONSTANT, not derived from the overview response. Deriving it would make the
 * plan change the moment the overview landed, which changes the request key,
 * which refetches the whole screen — every load would cost two batches. A key
 * countWindow does not return simply never gets a tile: the strip is filtered
 * against the response.
 */
export const HEADLINE_KPI_KEYS = [
  'entries',
  'people_admitted',
  'incidents',
  'packages_logged',
  'packages_pending',
  'arrivals_still_open',
];

/**
 * Tile key -> the breakdown measure whose split over time is the SAME figure.
 *
 * Only pairs that are the same number are here. `packages_pending` is absent on
 * purpose: the shelf is an as-of figure and `shelf` has no `day` dimension at
 * all, so a per-day sparkline under it would be a different measurement wearing
 * the tile's label. `arrivals_still_open` is absent for the same reason — it is
 * live, and a sparkline of a live figure is a category error.
 */
export const KPI_SPARK_MEASURE: Record<string, string> = {
  entries: 'entries',
  incidents: 'incidents',
  people_admitted: 'people',
  packages_logged: 'parcels_in',
  packages_picked_up: 'parcels_out',
  visitor_entries: 'arrivals',
};

/**
 * The labels. The one thing this screen cannot read off the wire —
 * /analytics/meta describes the breakdown MEASURES, not the read-model KPI keys.
 * `humanise` is the fallback, so an unlabelled new figure reads as "Packages
 * redacted" rather than as `packages_redacted`.
 */
export const KPI_LABELS: Record<string, string> = {
  entries: 'Log entries',
  visitor_entries: 'Arrivals logged',
  // The table is daily_log; every user-facing surface says Building Activities.
  daily_entries: 'Building activities',
  people_admitted: 'People admitted',
  emergency_headcount: 'Emergency headcount',
  // Counts visitor_log rows of kind Delivery — PEOPLE who delivered. The
  // packages_* figures count PARCELS. Different numbers, both correct, and the
  // labels have to keep them apart.
  deliveries_logged: 'Delivery arrivals',
  packages_pending: 'Parcels on the shelf',
  packages_logged: 'Parcels taken in',
  packages_picked_up: 'Parcels handed over',
  packages_returned: 'Parcels returned',
  exits_auto_closed: 'Exits closed by the sweep',
  exits_desk_recorded: 'Exits recorded at the desk',
  arrivals_still_open: 'People still inside',
  activities_still_open: 'Activities still open',
  incidents: 'Incidents',
  follow_ups: 'Follow-ups',
};

export const COUNTED_AS: Record<string, string> = {
  window: 'over the period',
  as_of: 'as of the end of the period',
  live: 'as of now',
};

/**
 * The time dimension for a granularity.
 *
 * `week` is a legal breakdown dimension that NO panel on the old screen ever
 * requested — one of the free wins this consolidation picks up. `month` has no
 * server-side dimension at all, so it is served by asking for `day` and
 * bucketing in the browser; the plan says `day` and the panel does the rest.
 */
export const timeDimension = (granularity: Granularity): string =>
  granularity === 'week' ? 'week' : 'day';

export interface PlanContext {
  filter: DashboardFilter;
  meta: FilterMeta;
  /** True when the caller can see more than one portfolio. */
  showPortfolios: boolean;
  maxPanels: number;
}

const dimensionsOf = (meta: FilterMeta, measureKey: string): string[] =>
  meta.measures.find(m => m.key === measureKey)?.dimensions || [];

export const legalPair = (
  meta: FilterMeta,
  measureKey: string,
  dimension: string
): boolean => dimensionsOf(meta, measureKey).includes(dimension);

/**
 * THE PLAN.
 *
 * THE ONE RULE THIS FUNCTION EXISTS FOR: never send a pair the measure has no
 * meaning for. The server substitutes and says so, but a control that offers an
 * impossible split is a bug in the UI regardless.
 */
export const buildPlan = ({
  filter,
  meta,
  showPortfolios,
  maxPanels,
}: PlanContext): AnalyticsPanelRequest[] => {
  const out: AnalyticsPanelRequest[] = [];
  const time = timeDimension(filter.granularity);

  const push = (id: string, measure: string, dimension: string, limit?: number) => {
    if (legalPair(meta, measure, dimension)) out.push({ id, measure, dimension, limit });
  };

  push(PANEL.pulseEntries, 'entries', time, DAY_LIMIT);
  push(PANEL.pulseIncidents, 'incidents', time, DAY_LIMIT);
  push(PANEL.league, filter.measure, 'building');
  push(PANEL.composition, filter.measure, 'kind');
  push(PANEL.severity, filter.measure, 'severity');
  push(PANEL.hour, filter.measure, 'hour_of_day');
  push(PANEL.staff, filter.measure, 'staff', STAFF_LIMIT);
  /* Only when it would say something the league panel does not: with the measure
     already set to incidents the scatter would be a diagonal line. */
  if (filter.measure !== 'incidents') push(PANEL.leagueIncidents, 'incidents', 'building');
  if (showPortfolios) push(PANEL.portfolio, filter.measure, 'portfolio');

  // Sparklines LAST, so they are the first thing trimmed if the plan ever grows
  // past the server's ceiling: a tile without its sparkline is still a figure,
  // where a panel without its data is a hole.
  for (const key of HEADLINE_KPI_KEYS) {
    const measure = KPI_SPARK_MEASURE[key];
    if (!measure) continue;
    // Already fetched for the pulse. Reusing the same result rather than asking
    // twice keeps the tile and the chart numerically identical, which is the
    // whole reason the sparkline is the same measure and not a lookalike.
    if (measure === 'entries' || measure === 'incidents') continue;
    push(`${SPARK_PREFIX}${key}`, measure, time, DAY_LIMIT);
  }

  return out.length > maxPanels ? out.slice(0, maxPanels) : out;
};

/**
 * The panel titles the CSV and the PDF name a panel by, so a file says what the
 * screen said.
 */
export const panelTitles = (measureLabel: string): Record<string, string> => ({
  [PANEL.pulseEntries]: 'Pulse — log entries per day',
  [PANEL.pulseIncidents]: 'Pulse — incidents per day',
  [PANEL.league]: `Building league — ${measureLabel}`,
  [PANEL.composition]: `Composition — ${measureLabel} by kind`,
  [PANEL.severity]: `Severity — ${measureLabel}`,
  [PANEL.portfolio]: `Portfolio roll-up — ${measureLabel}`,
  [PANEL.hour]: `Hour of day — ${measureLabel}`,
  [PANEL.staff]: `Who recorded it — ${measureLabel}`,
  [PANEL.leagueIncidents]: 'Incidents by building',
  [PANEL.reportsDaily]: 'People logged each day',
  [PANEL.reportsKind]: 'By kind',
});
